import React, { useState, useEffect, useRef } from "react";
import {
  Search, Eye, Edit, X, AlertCircle, Plus, Trash2,
  Megaphone, Building2, Image, Calendar, ChevronDown,
  Upload, FileX
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import {
  listAdminAnnouncements,
  showAdminAnnouncement,
  createAdminAnnouncement,
  updateAdminAnnouncement,
  deleteAdminAnnouncement,
  uploadAnnouncementMedia,
  deleteAnnouncementMedia,
  listAllCompanies
} from "../../services/adminAnnouncements";

const PRIORITY_LABELS = { 1: "Alta", 2: "Média", 3: "Baixa" };
const PRIORITY_COLORS = { 1: "ativo", 2: "pendente", 3: "inativo" };

const Announcements = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", targetCompanyId: "", status: "", type: "" });
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: "create", item: null });
  const [detailModal, setDetailModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null, loading: false });
  const [mediaModal, setMediaModal] = useState({ open: false, item: null });
  const fileInputRef = useRef(null);

  useEffect(() => {
    load(1);
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const data = await listAllCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch { /* optional */ }
  };

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const query = { pageNumber: page, pageSize: 20, sortBy: "createdAt", sortOrder: "DESC" };
      if (filters.search) query.search = filters.search;
      if (filters.targetCompanyId) query.targetCompanyId = filters.targetCompanyId;
      if (filters.status !== "") query.status = filters.status;
      if (filters.type) query.type = filters.type;
      const data = await listAdminAnnouncements(query);
      setItems(data.items || []);
      setPagination(data.pagination || {});
    } catch (err) {
      setError(err.message || "Erro ao carregar informativos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => { e.preventDefault(); load(1); };
  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => { setFilters({ search: "", targetCompanyId: "", status: "", type: "" }); setTimeout(() => load(1), 0); };

  const openCreate = () => setModal({ open: true, mode: "create", item: { title: "", text: "", priority: 2, status: true, targetCompanyId: null, type: "general" } });
  const openEdit = async (row) => {
    try {
      const data = await showAdminAnnouncement(row.id);
      setModal({ open: true, mode: "edit", item: data });
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao buscar informativo" }); }
  };
  const openDetail = async (row) => {
    try {
      const data = await showAdminAnnouncement(row.id);
      setDetailModal({ open: true, item: data });
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao buscar informativo" }); }
  };
  const openMedia = async (row) => {
    try {
      const data = await showAdminAnnouncement(row.id);
      setMediaModal({ open: true, item: data });
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao buscar informativo" }); }
  };

  const handleSave = async () => {
    const { mode, item } = modal;
    if (!item.title?.trim()) { setToast({ type: "error", message: "Título é obrigatório" }); return; }
    if (!item.text?.trim()) { setToast({ type: "error", message: "Texto é obrigatório" }); return; }
    try {
      if (mode === "create") {
        await createAdminAnnouncement({
          title: item.title, text: item.text, priority: Number(item.priority),
          status: item.status, targetCompanyId: item.targetCompanyId || null, type: item.type || "general"
        });
        setToast({ type: "success", message: "Informativo criado com sucesso" });
      } else {
        await updateAdminAnnouncement(item.id, {
          title: item.title, text: item.text, priority: Number(item.priority),
          status: item.status, targetCompanyId: item.targetCompanyId || null, type: item.type || "general"
        });
        setToast({ type: "success", message: "Informativo atualizado com sucesso" });
      }
      setModal({ open: false, mode: "create", item: null });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao salvar" }); }
  };

  const handleDelete = async () => {
    if (!deleteModal.item) return;
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await deleteAdminAnnouncement(deleteModal.item.id);
      setToast({ type: "success", message: "Informativo excluído" });
      setDeleteModal({ open: false, item: null, loading: false });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao excluir" }); setDeleteModal(prev => ({ ...prev, loading: false })); }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !mediaModal.item) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await uploadAnnouncementMedia(mediaModal.item.id, formData);
      setToast({ type: "success", message: "Mídia enviada" });
      const updated = await showAdminAnnouncement(mediaModal.item.id);
      setMediaModal({ open: true, item: updated });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro no upload" }); }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleMediaDelete = async () => {
    if (!mediaModal.item) return;
    try {
      await deleteAnnouncementMedia(mediaModal.item.id);
      setToast({ type: "success", message: "Mídia removida" });
      const updated = await showAdminAnnouncement(mediaModal.item.id);
      setMediaModal({ open: true, item: updated });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao remover mídia" }); }
  };

  const columns = [
    { header: "ID", accessor: "id", render: (row) => <span className="font-mono text-purple-600 dark:text-purple-400">#{row.id}</span> },
    { header: "Título", accessor: "title", render: (row) => <span className="font-bold text-gray-900 dark:text-gray-100 block truncate max-w-[200px]" title={row.title}>{row.title}</span> },
    {
      header: "Prioridade", render: (row) =>
        <StatusBadge status={PRIORITY_COLORS[row.priority] || "default"} label={PRIORITY_LABELS[row.priority] || "—"} />
    },
    {
      header: "Status", render: (row) =>
        <StatusBadge status={row.status ? "active" : "inactive"} label={row.status ? "Ativo" : "Inativo"} />
    },
    { header: "Tipo", render: (row) => <span className="text-xs capitalize">{row.type || "general"}</span> },
    { header: "Empresa Destino", render: (row) => row.targetCompanyName || <span className="text-purple-500 font-semibold">Todas</span> },
    {
      header: "Mídia", render: (row) =>
        row.mediaPath
          ? <button onClick={() => openMedia(row)} className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline text-xs"><Image size={12} /> Ver</button>
          : <span className="text-gray-400">—</span>
    },
    {
      header: "Criado em", render: (row) =>
        <span className="text-xs text-gray-500 dark:text-gray-400">{new Date(row.createdAt).toLocaleDateString("pt-BR")}</span>
    },
    {
      header: "Ações", render: (row) => (
        <div className="flex items-center justify-end gap-1.5">
          <button onClick={() => openDetail(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors" title="Visualizar">
            <Eye size={15} />
          </button>
          <button onClick={() => openEdit(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors" title="Editar">
            <Edit size={15} />
          </button>
          <button onClick={() => setDeleteModal({ open: true, item: row, loading: false })} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 transition-colors" title="Excluir">
            <Trash2 size={15} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Comunicação"
        title="Informativos"
        subtitle={`${pagination.total || 0} informativo(s) na plataforma`}
        action={
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">
            <Plus size={14} /> Novo Informativo
          </button>
        }
      />

      {error && (
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${
          toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      <form onSubmit={handleSearchSubmit} className="flex gap-2.5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-3 text-gray-400 dark:text-gray-500" />
          <input type="text" value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Buscar por título ou conteúdo..." className="w-full pl-9 pr-4 py-2 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-purple-500" />
        </div>
        <button type="submit" className="px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">Filtrar</button>
        <button type="button" onClick={clearFilters} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl">Limpar</button>
      </form>

      <div className="flex flex-wrap gap-2">
        <select value={filters.targetCompanyId} onChange={(e) => handleFilterChange("targetCompanyId", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todas as empresas</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todos os status</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
        <select value={filters.type} onChange={(e) => handleFilterChange("type", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todos os tipos</option>
          <option value="general">Geral</option>
          <option value="birthday">Aniversário</option>
          <option value="system">Sistema</option>
        </select>
      </div>

      <DataTable columns={columns} data={items} loading={loading} emptyTitle="Nenhum informativo encontrado" emptyDescription="Crie o primeiro informativo da plataforma." />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
          <span>Página {pagination.pageNumber} de {pagination.totalPages} ({pagination.total} informativos)</span>
          <div className="flex gap-2">
            <button disabled={pagination.pageNumber <= 1} onClick={() => load(pagination.pageNumber - 1)} className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10">Anterior</button>
            <button disabled={pagination.pageNumber >= pagination.totalPages} onClick={() => load(pagination.pageNumber + 1)} className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10">Próxima</button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-2xl p-6 border border-gray-200 dark:border-white/10 relative space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{modal.mode === "create" ? "Novo Informativo" : "Editar Informativo"}</h3>
              <button onClick={() => setModal({ open: false, mode: "create", item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Título *</label>
                <input type="text" value={modal.item?.title || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, title: e.target.value } }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" placeholder="Título do informativo" />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Conteúdo *</label>
                <textarea value={modal.item?.text || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, text: e.target.value } }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" rows={5} placeholder="Conteúdo do informativo" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Prioridade</label>
                  <select value={modal.item?.priority || 2} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, priority: Number(e.target.value) } }))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500">
                    <option value={1}>Alta</option>
                    <option value={2}>Média</option>
                    <option value={3}>Baixa</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Empresa Destino</label>
                  <select value={modal.item?.targetCompanyId || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, targetCompanyId: e.target.value ? Number(e.target.value) : null } }))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500">
                    <option value="">Todas</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Status</label>
                  <select value={modal.item?.status ? "true" : "false"} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, status: e.target.value === "true" } }))}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500">
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
              <button onClick={() => setModal({ open: false, mode: "create", item: null })} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">Cancelar</button>
              <button onClick={handleSave} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">
                {modal.mode === "create" ? "Criar" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModal.open && detailModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-2xl p-6 border border-gray-200 dark:border-white/10 relative space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{detailModal.item.title}</h3>
              <button onClick={() => setDetailModal({ open: false, item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={PRIORITY_COLORS[detailModal.item.priority] || "default"} label={PRIORITY_LABELS[detailModal.item.priority] || "—"} />
                <StatusBadge status={detailModal.item.status ? "active" : "inactive"} label={detailModal.item.status ? "Ativo" : "Inativo"} />
                <span className="text-xs text-gray-500 capitalize">{detailModal.item.type}</span>
              </div>
              {detailModal.item.targetCompanyName && (
                <p className="text-xs text-gray-600 dark:text-gray-300"><Building2 size={12} className="inline mr-1" /> Destino: {detailModal.item.targetCompanyName}</p>
              )}
              {!detailModal.item.targetCompanyId && (
                <p className="text-xs text-purple-500 font-semibold"><Building2 size={12} className="inline mr-1" /> Destino: Todas as empresas</p>
              )}
              <div className="rounded-xl bg-gray-50 dark:bg-[#191c1e] p-4 text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{detailModal.item.text}</div>
              {detailModal.item.mediaPath && (
                <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
                  <img src={detailModal.item.mediaPath} alt={detailModal.item.mediaName || "Mídia"} className="max-w-full max-h-64 object-contain mx-auto" />
                </div>
              )}
              <p className="text-xs text-gray-400">Criado em: {new Date(detailModal.item.createdAt).toLocaleString("pt-BR")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-md p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Confirmar Exclusão</h3>
              <button onClick={() => setDeleteModal({ open: false, item: null, loading: false })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300">Deseja excluir o informativo <strong>{deleteModal.item?.title}</strong>?</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
              <button onClick={() => setDeleteModal({ open: false, item: null, loading: false })} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">Cancelar</button>
              <button onClick={handleDelete} disabled={deleteModal.loading} className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-xs font-bold text-white rounded-xl">
                {deleteModal.loading ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Modal */}
      {mediaModal.open && mediaModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-lg p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Mídia — {mediaModal.item.title}</h3>
              <button onClick={() => setMediaModal({ open: false, item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>

            {mediaModal.item.mediaPath ? (
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
                  <img src={mediaModal.item.mediaPath} alt={mediaModal.item.mediaName || "Mídia"} className="max-w-full max-h-64 object-contain mx-auto" />
                </div>
                <p className="text-xs text-gray-500">{mediaModal.item.mediaName}</p>
                <div className="flex gap-2">
                  <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">
                    <Upload size={12} /> Substituir
                  </button>
                  <button onClick={handleMediaDelete} className="flex items-center gap-1 px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-xs font-bold text-white rounded-xl">
                    <FileX size={12} /> Remover
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">Nenhuma mídia associada.</p>
                <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">
                  <Upload size={12} /> Enviar Mídia
                </button>
              </div>
            )}

            <input ref={fileInputRef} type="file" accept="image/*,video/*,application/pdf" onChange={handleMediaUpload} className="hidden" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;
